import { useEffect } from 'react'
import type { Product } from '../../types'
import { useCategoryStore } from '../../store/categoryStore'
import ErrorMsg from '../../components/ui/ErrorMsg'
import { inputClass, labelClass } from '../../components/ui/inputClass'
import { Asterisk } from 'lucide-react'

type FormData = Omit<Product, 'id' | 'images' | 'media' | 'createdAt' | 'updatedAt'>

interface Props {
  form: FormData
  errors: Record<string, string>
  set: (field: keyof FormData, value: string | number) => void
}

export default function FormFields({ form, errors, set }: Props) {
  const { categories, fetchCategories, getTree } = useCategoryStore()
  const tree = getTree()

  useEffect(() => {
    if (categories.length === 0) fetchCategories()
  }, [categories.length, fetchCategories])

  return (
    <>
      <div>
        <label className={`${labelClass} flex gap-1`} htmlFor="name">שם המוצר <Asterisk className="h-2.5 w-2.5 text-[#E74C3C]" /></label>
        <input
          id="name"
          value={form.name}
          onChange={e => set('name', e.target.value)}
          placeholder="לדוגמה: תפוחים פרימיום"
          aria-invalid={!!errors.name}
          className={inputClass(errors.name)}
        />
        <ErrorMsg message={errors.name} />
      </div>

      <div>
        <label className={`${labelClass} flex gap-1`} htmlFor="price">מחיר (₪) <Asterisk className="h-2.5 w-2.5 text-[#E74C3C]" /></label>
        <input id="price" type="number" min={0}
          value={form.price}
          onChange={e => set('price', parseFloat(e.target.value) || 0)}
          aria-invalid={!!errors.price}
          className={`${inputClass(errors.price)} appearance-none h-[42px]`}
        />
        <ErrorMsg message={errors.price} />
      </div>

      <div className="grid grid-cols-3 gap-4">

      <div>
          <label className={`${labelClass} flex gap-1`} htmlFor="custom_sku">מק"ט מוצר</label>
          <input
            id="custom_sku"
            value={form.custom_sku ?? ''}
            onChange={e => set('custom_sku', e.target.value)}
            placeholder="123456-78-9"
            className={inputClass(errors.custom_sku)}
          />
          <ErrorMsg message={errors.custom_sku} />
        </div>
        
        <div>
          <label className={labelClass} htmlFor="quantity">כמות זמינה</label>
          <input id="quantity" type="number" min={0}
            value={form.quantity}
            onChange={e => set('quantity', parseInt(e.target.value) || 0)}
            aria-invalid={!!errors.quantity}
            className={`${inputClass(errors.quantity)} appearance-none h-[42px]`}
          />
          <ErrorMsg message={errors.quantity} />
        </div>

        <div>
          <label className={labelClass} htmlFor="unit">יחידה</label>
          <select id="unit" value={form.unit}
            onChange={e => set('unit', e.target.value)}
            className={`${inputClass(errors.unit)} appearance-none h-[42px]`}
          >
            <option value="kg">ק"ג</option>
            <option value="unit">יחידה</option>
            <option value="carton">קרטון</option>
            <option value="pallet">משטח</option>
            <option value="container">מיכל</option>
          </select>
        </div>

      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass} htmlFor="category_id">קטגוריה</label>
          <select
            id="category_id"
            value={form.category_id ?? ''}
            onChange={e => set('category_id', e.target.value)}
            className={`${inputClass(errors.category_id)} appearance-none h-[42px]`}
          >
            <option value="">בחר קטגוריה</option>
            {tree.map(root => (
              <optgroup key={root.id} label={root.name}>
                <option value={root.id}>{root.name} (כללי)</option>
                {root.children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <ErrorMsg message={errors.category_id} />
        </div>
        
        <div>
          <label className={labelClass} htmlFor="status">סטטוס</label>
          <select id="status" value={form.status}
            onChange={e => set('status', e.target.value)}
            className={`${inputClass(errors.status)} appearance-none h-[42px]`}
          >
            <option value="active">מכירה פעילה</option>
            <option value="sold_out">אזל</option>
            <option value="suspended">הושהה</option>
            <option value="draft">טיוטה</option>
            <option value="other">אחר</option>
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className={labelClass} htmlFor="description">תיאור</label>
          <span className={`text-[11px] font-medium tabular-nums transition-colors ${
            (form.description?.length ?? 0) > 900
              ? (form.description?.length ?? 0) >= 1000 ? 'text-[#E74C3C]' : 'text-[#F0A500]'
              : 'text-text-muted'
          }`}>
            {form.description?.length ?? 0} / 1000
          </span>
        </div>
        <textarea
          id="description"
          value={form.description}
          onChange={e => set('description', e.target.value.slice(0, 1000))}
          placeholder="תיאור קצר של המוצר..."
          rows={3}
          maxLength={1000}
          className={`${inputClass(errors.description)} resize-none`}
        />
        <ErrorMsg message={errors.description} />
      </div>
    </>
  )
}